// src/app/services/seo.service.ts
import { Injectable, Inject, inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

export type AppLang = 'en' | 'fr' | 'es' | 'hi' | 'id' | 'pt' | 'tr' | 'vi' | 'it';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private platformId = inject(PLATFORM_ID);

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private doc: Document
  ) { }

  private setTag(nameOrProp: { name?: string; property?: string }, content: string) {
    const selector = nameOrProp.name
      ? `name='${nameOrProp.name}'`
      : `property='${nameOrProp.property}'`;
    const existing = this.meta.getTag(selector);
    if (existing) this.meta.removeTagElement(existing);
    this.meta.addTag({ ...nameOrProp, content });
  }

  /** SSR-safe: utilise DOCUMENT (OK côté serveur) */
  setHtmlLang(lang: AppLang) {
    this.doc?.documentElement?.setAttribute('lang', lang);
  }

  /** Canonical SSR-safe */
  setCanonical(url: string) {
    if (!url) return;
    let link = this.doc.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /** (Optionnel) hreflang SSR-safe */
  setHreflang(alternates: Record<string, string>) {
    Object.entries(alternates || {}).forEach(([lang, href]) => {
      const selector = `link[rel='alternate'][hreflang='${lang}']`;
      let link = this.doc.querySelector<HTMLLinkElement>(selector);
      if (!link) {
        link = this.doc.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        this.doc.head.appendChild(link);
      }
      link.setAttribute('href', href);
    });
  }

  setSeoForLang(lang: AppLang, absoluteUrl = 'https://swaptokens.app/') {
    const cfg =
      lang === 'fr'
        ? {
          title: 'SwapTokens - Comparez et optimisez vos swaps DeFi',
          desc:
            'SwapTokens compare en temps réel les meilleurs taux sur Ethereum, Arbitrum, Polygon et BNB Chain. Obtenez le meilleur prix via Uniswap, 1inch, Curve, Balancer, OpenOcean et plus.',
        }
        : lang === 'es'
          ? {
            title: 'SwapTokens - Compare y optimice sus swaps DeFi',
            desc:
              'SwapTokens compara en tiempo real las mejores tasas en Ethereum, Arbitrum, Polygon y BNB Chain. Obtenga el mejor precio con Uniswap, 1inch, Curve, Balancer, OpenOcean y más.',
          }
          : lang === 'hi'
            ? {
              title: 'SwapTokens - अपने DeFi स्वैप्स की तुलना करें और उन्हें बेहतर बनाएं',
              desc:
                'SwapTokens Ethereum, Arbitrum, Polygon और BNB Chain पर रीयल-टाइम में सर्वश्रेष्ठ स्वैप रेट्स की तुलना करता है। Uniswap, 1inch, Curve, Balancer, OpenOcean आदि से सर्वोत्तम कीमत पाएं।',
            }
            : {
              title: 'SwapTokens - Compare and optimize your DeFi swaps',
              desc:
                'SwapTokens compares real-time swap rates across Ethereum, Arbitrum, Polygon and BNB Chain. Get the best price from Uniswap, 1inch, Curve, Balancer, OpenOcean and more.',
            };

    this.setHtmlLang(lang);
    this.title.setTitle(cfg.title);
    this.setTag({ name: 'description' }, cfg.desc);

    // Open Graph
    this.setTag({ property: 'og:type' }, 'website');
    this.setTag({ property: 'og:title' }, cfg.title);
    this.setTag({ property: 'og:description' }, cfg.desc);
    this.setTag({ property: 'og:image' }, 'https://swaptokens.app/assets/icons/og-image.png');
    this.setTag({ property: 'og:url' }, absoluteUrl);

    // Twitter
    this.setTag({ name: 'twitter:card' }, 'summary_large_image');
    this.setTag({ name: 'twitter:title' }, cfg.title);
    this.setTag({ name: 'twitter:description' }, cfg.desc);
    this.setTag({ name: 'twitter:image' }, 'https://swaptokens.app/assets/icons/og-image.png');
    this.setTag({ name: 'twitter:site' }, '@SwapTokensApp');

    // Canonical (et hreflang si tu passes la map)
    this.setCanonical(absoluteUrl);
  }
}
